import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { createLogger } from '@/lib/logging';

const logger = createLogger('OrdersMasterDetail');
import { ListFilter, Calendar, AlertCircle, Clock, FileText, Shield, Users, ExternalLink, ArrowRight, ShieldCheck, ShieldAlert, CheckCircle2, AlertTriangle, Briefcase, MapPin, User, BookOpen, Tag, Download } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { withErrorBoundary } from '@/components/error-boundary/withErrorBoundary';
import { useAuth } from '@clerk/clerk-react';
import { ProductOrderService } from '@/domain/product-orders/services/ProductOrderService';
import { ProductOrder } from '@/domain/product-orders/types/product-order';
import { toast } from 'sonner';
import { OrderHistoryService, DocumentFile, ScreenedEntity } from '@/domain/order-history/services/OrderHistoryService';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { cn } from '@/lib/utils';
import { EntityBadge } from '@/components/ui/entity-badge';
import { httpRequest } from '@/lib/http-client';
import { ShippingFlow } from './shipping-flow';
import { TransactionTimeline } from './transaction-timeline';
import { useDocumentStore } from '@/stores/useDocumentStore';

interface OrdersMasterDetailProps {
  trigger?: React.ReactNode;
  initialOrderId?: string;
  order?: ProductOrder;
}

function OrdersMasterDetailComponent({ trigger, initialOrderId, order }: OrdersMasterDetailProps) {
  const navigate = useNavigate();
  
  // Order list state
  const [orders, setOrders] = useState<ProductOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilterTab, setActiveFilterTab] = useState('day');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Selected order state
  const [selectedOrder, setSelectedOrder] = useState<ProductOrder | null>(null);
  const [orderHighlights, setOrderHighlights] = useState<Record<string, any>>({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Auth hook
  const { getToken } = useAuth();

  // Fetch order list when popup opens or filter changes
  useEffect(() => {
    if (open) {
      fetchOrders();
    }
  }, [open, activeFilterTab]);

  // Fetch order details when an order is selected
  useEffect(() => {
    if (selectedOrder) {
      fetchOrderHighlights(selectedOrder.id);
    }
  }, [selectedOrder]);

  // When the popup is opened and initialOrderId is provided, find and select that order
  useEffect(() => {
    if (open && initialOrderId && orders.length > 0) {
      const orderToSelect = orders.find(order => order.id === initialOrderId);
      if (orderToSelect) {
        setSelectedOrder(orderToSelect);
      }
    }
  }, [open, initialOrderId, orders]);

  // When the popup is opened and a direct order object is provided, select it
  useEffect(() => {
    if (open && order) {
      setSelectedOrder(order);
    }
  }, [open, order]);

  // Function to open an order in the main view
  const openOrderInMainView = async (order: ProductOrder) => {
    if (useDocumentStore.getState().isProcessing) {
      toast.info('Wait for verification to finish before opening another order.');
      return;
    }
    try {
      logger.debug('Opening order', { orderId: order.id });
      
      // The main view restores both saved results and an in-progress run.
      // Preloading only a snapshot here would bypass run recovery.
      
      // Close dialog and navigate to main view
      setOpen(false);
      navigate(`/app?order=${encodeURIComponent(order.id)}`);
      
    } catch (error) {
      logger.error('Error opening order', { error });
      
      // Show a more specific error message
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Failed to open order';
      
      // Special handling for 404 errors
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        toast.error(`Order ${order.order_number} could not be found. It may have been deleted.`);
      } else {
        toast.error(`Failed to open order: ${errorMessage}`);
      }
    }
  };

  // Fetch orders list
  const fetchOrders = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = await getToken({ skipCache: true });
      
      if (!token) {
        setError('Authentication token not available. Please login again.');
        setIsLoading(false);
        return;
      }
      
      let days;
      
      if (activeFilterTab === 'day') {
        days = 1;
      } else if (activeFilterTab === '3days') {
        days = 3;
      } else {
        days = 30;
      }
      
      // Build the query parameters
      const queryParams = new URLSearchParams();
      queryParams.append('limit', '100'); // Increased limit to get more orders for client-side filtering
      if (days) queryParams.append('days', days.toString());
      
      const endpoint = `/api/orders?${queryParams.toString()}`;
      
      const result = await httpRequest<ProductOrder[]>(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      logger.debug('Orders fetched', { count: result.length, days });

      // If the server didn't apply the filter properly, filter client-side
      let filteredOrders = result;
      if (days < 30 && result.length > 20) {
        const now = new Date();
        const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        
        filteredOrders = result.filter(order => {
          const orderDate = new Date(order.created_at);
          return orderDate >= cutoffDate;
        });
        
        logger.debug('Client-side filtering applied', { before: result.length, after: filteredOrders.length });
      }
      
      setOrders(filteredOrders);
      
      // Select the first order by default if no order is selected
      if (filteredOrders.length > 0 && !selectedOrder) {
        setSelectedOrder(filteredOrders[0]);
      }
    } catch (error) {
      logger.error('Error fetching orders', { error });
      setError(error instanceof Error ? error.message : 'Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch highlights for a specific order
  const fetchOrderHighlights = async (orderId: string) => {
    setDetailsLoading(true);
    try {
      const token = await getToken({ skipCache: true });
      if (!token) {
        logger.error('Authentication token not available for order highlights');
        return;
      }
      // Screening and files cover every file in the order. The latest saved
      // result only adds the transaction timeline and shipping route.
      const [screening, documents, snapshot] = await Promise.all([
        OrderHistoryService.getOrderScreening(orderId),
        OrderHistoryService.getOrderDocuments(orderId, token),
        OrderHistoryService.getOrderEntities(orderId, token).catch(error => {
          logger.error('Error fetching entity snapshot', { orderId, error });
          return null;
        }),
      ]);
      const extendedData = snapshot?.entities && !Array.isArray(snapshot.entities) ? snapshot.entities.extendedData ?? null : null;
      setOrderHighlights(prev => ({ ...prev, [orderId]: { screening, documents, extendedData } }));
    } catch (error) {
      logger.error('Error fetching highlights for order', { orderId, error });
      setOrderHighlights(prev => ({ ...prev, [orderId]: { error: true } }));
    } finally {
      setDetailsLoading(false);
    }
  };

  const downloadDocument = async (document: DocumentFile) => {
    try {
      await OrderHistoryService.downloadDocument(document);
    } catch (error) {
      logger.error('Error downloading document', { error });
      toast.error(`Could not download ${document.file_name}`);
    }
  };

  // Helper function to determine badge class based on status
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-amber-100 text-amber-700';
      case 'processing':
        return 'bg-blue-100 text-blue-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'archived':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper to truncate text with ellipsis
  const truncateText = (text: string, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // Render the list of orders (master view)
  const renderOrdersList = () => {
    if (error) {
      return (
        <Alert variant="destructive" className="mt-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (isLoading) {
      return (
        <div className="space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-border">
              <div className="space-y-2">
                <Skeleton className="h-4 w-[200px]" />
                <Skeleton className="h-3 w-[150px]" />
              </div>
              <Skeleton className="h-6 w-[100px]" />
            </div>
          ))}
        </div>
      );
    }

    if (orders.length === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">No orders found</p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        {orders.map((order) => (
          <div 
            key={order.id} 
            className={cn(
              "flex flex-col py-2 border-b border-border hover:bg-muted/30 rounded-sm px-2 transition-colors cursor-pointer",
              selectedOrder?.id === order.id ? "bg-muted" : ""
            )}
            onClick={() => setSelectedOrder(order)}
          >
            <div className="flex items-center justify-between mb-1">
              <div className="font-medium text-sm">
                Order {order.order_number}
              </div>
              <div className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadgeClass(order.status)}`}>
                {order.status}
              </div>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {format(new Date(order.created_at), 'MMM d, yyyy - h:mm a')}
            </div>
            {order.manual_order_number && (
              <div className="text-xs text-muted-foreground mt-0.5" title={order.manual_order_number}>
                Manual #: {truncateText(order.manual_order_number, 15)}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render order details (detail view)
  const renderOrderDetails = () => {
    if (!selectedOrder) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <AlertCircle className="h-12 w-12 opacity-20 mb-2" />
          <p className="text-muted-foreground">Select an order to view details</p>
        </div>
      );
    }

    const highlights = orderHighlights[selectedOrder.id];
    const isLoading = detailsLoading || !highlights;

    return (
      <div className="px-1">
        <div className="mb-4 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              Order {selectedOrder.order_number}
              {selectedOrder.manual_order_number && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  (Manual #: {truncateText(selectedOrder.manual_order_number, 20)})
                </span>
              )}
            </h3>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
              <Calendar className="h-3.5 w-3.5" />
              <span>{format(new Date(selectedOrder.created_at), 'MMM d, yyyy - h:mm a')}</span>
            </div>
          </div>
          
          <Button
              onClick={() => openOrderInMainView(selectedOrder)}
              className="gap-2"
              disabled={isLoading}
            >
              Open Order <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
            <Skeleton className="h-[120px] w-full" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Documents Card */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                {!highlights.documents?.length ? (
                  <p className="text-sm text-muted-foreground">No documents found</p>
                ) : (
                  <div className="space-y-1">
                    {highlights.documents.map((doc: DocumentFile) => (
                      <div key={doc.id} className="flex items-center justify-between gap-2">
                        <span className="text-sm truncate" title={doc.file_name}>{doc.file_name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 shrink-0"
                          onClick={() => downloadDocument(doc)}
                          aria-label={`Download ${doc.file_name}`}
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Screening across every file in the order */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-base font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-primary" />
                  Sanctions screening
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-3">
                {highlights.error ? (
                  <p className="text-sm text-destructive">Screening results could not be loaded. Select the order again to retry.</p>
                ) : highlights.screening.status === 'none' ? (
                  <p className="text-sm text-muted-foreground">No screening results saved for this order</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Status</span>
                      {highlights.screening.status === 'flagged' ? (
                        <Badge className="bg-red-50 text-red-700 border border-red-200 gap-1">
                          <AlertTriangle className="h-4 w-4" />
                          Flagged
                        </Badge>
                      ) : (
                        <Badge variant="default" className="bg-green-600 gap-1">
                          <CheckCircle2 className="h-4 w-4" />
                          No matches
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Names screened</span>
                      <span className="text-sm">{highlights.screening.screenedCount}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Potential matches</span>
                      <span className="text-sm">{highlights.screening.flaggedCount}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {!highlights.error && highlights.screening.entities.length > 0 && (
              <Card className="md:col-span-2">
                <CardHeader className="py-3">
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    Screened names
                  </CardTitle>
                </CardHeader>
                <CardContent className="pb-3 space-y-2">
                  {highlights.screening.entities.map((entity: ScreenedEntity) => (
                    <div
                      key={entity.name}
                      className={cn('p-2 rounded-md', entity.flagged
                        ? 'bg-red-50/70 border-l-2 border-l-red-500'
                        : 'border border-slate-200')}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <EntityBadge type={entity.type === 'individual' ? 'individual' : 'organization'} />
                          <span className="font-medium text-sm truncate">{entity.name}</span>
                        </div>
                        {entity.flagged ? (
                          <Badge className="bg-red-50 text-red-700 border border-red-200 gap-1 shrink-0">
                            <AlertTriangle className="h-4 w-4" />
                            Flagged
                          </Badge>
                        ) : (
                          <Badge className="bg-green-50 text-green-700 border border-green-200 gap-1 shrink-0">
                            <CheckCircle2 className="h-4 w-4" />
                            No matches
                          </Badge>
                        )}
                      </div>
                      {entity.topMatch && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Possible match: {entity.topMatch.name} ({Math.round(entity.topMatch.score * 100)}%)
                          {entity.topMatch.topics.length > 0 && ` · ${entity.topMatch.topics.map(topic => topic.replace('.', ' ')).join(', ')}`}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Transaction Timeline */}
            {highlights?.extendedData?.documentFlow && (
              <Card className="md:col-span-2 mt-4">
                <TransactionTimeline 
                  documentFlow={highlights.extendedData.documentFlow}
                  className="border-none shadow-none"
                />
              </Card>
            )}
            
            {/* Shipping Flow */}
            {highlights?.extendedData?.shippingFlow && (
              <Card className="md:col-span-2 mt-4">
                <ShippingFlow 
                  shippingFlow={highlights.extendedData.shippingFlow}
                  className="border-none shadow-none"
                />
              </Card>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm" className="gap-2">
            <Clock className="h-4 w-4" />
            Order History
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[90vw] max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListFilter className="h-5 w-5" /> Order History
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col md:flex-row gap-4 h-[calc(80vh-100px)] overflow-hidden">
          {/* Master view (orders list) */}
          <div className="md:w-1/3 min-h-[200px] md:min-h-0 md:max-w-[300px] border-r pr-2">
            <Tabs 
              defaultValue="day" 
              value={activeFilterTab} 
              onValueChange={setActiveFilterTab} 
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="day">24h</TabsTrigger>
                <TabsTrigger value="3days">3 Days</TabsTrigger>
                <TabsTrigger value="all">All Orders</TabsTrigger>
              </TabsList>
              
              <ScrollArea className="h-[calc(80vh-170px)] rounded-md border p-4">
                {renderOrdersList()}
              </ScrollArea>
            </Tabs>
          </div>
          
          {/* Detail view (order details) */}
          <div className="flex-1 overflow-hidden pl-2">
            <ScrollArea className="h-[calc(80vh-100px)]">
              {renderOrderDetails()}
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Export with error boundary HOC for consistency with other components
export const OrdersMasterDetail = withErrorBoundary(OrdersMasterDetailComponent, {
  feature: 'orders-master-detail'
}); 
